// 🌿 API Craft Back Office - Main Layout Component
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavItem } from '../types';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  
  // This component only renders when user is authenticated, so user should exist

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems: NavItem[] = [
    { path: '/', label: 'Dashboard', icon: '🏠' },
    { path: '/models', label: 'Models', icon: '📊' },
    { path: '/ai-chat', label: 'AI Assistant', icon: '🤖' },
    { path: '/api-docs', label: 'API Docs', icon: '📚' },
    { path: '/profile', label: 'Profile', icon: '👤' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-mint-50 via-sage-50 to-emerald-50">
      {/* Navigation Header */}
      <nav className="nav-card sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo and main nav */}
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-mint-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-soft">
                    <span className="text-white text-xl font-bold">🌿</span>
                  </div>
                  <div>
                    <h1 className="text-xl font-bold bg-gradient-to-r from-forest-700 to-emerald-600 bg-clip-text text-transparent">
                      API Craft
                    </h1>
                    <p className="text-xs text-sage-500 -mt-1">Back Office</p>
                  </div>
                </div>
              </div>
              
              {/* Desktop navigation */}
              <div className="hidden sm:ml-8 sm:flex sm:space-x-2">
                {navItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 transform hover:scale-105 ${
                      isActive(item.path)
                        ? 'bg-gradient-to-r from-mint-100 to-emerald-100 text-forest-700 shadow-soft border-2 border-mint-200'
                        : 'text-sage-600 hover:text-forest-700 hover:bg-sage-50'
                    }`}
                  >
                    <span className="mr-2 text-lg">{item.icon}</span>
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* User menu */}
            <div className="hidden sm:ml-6 sm:flex sm:items-center">
              <div className="flex items-center space-x-4">
                {/* User info */}
                <div className="text-right">
                  <p className="text-sm font-medium text-forest-700">
                    Welcome back! 👋
                  </p>
                  <p className="text-xs text-sage-500 truncate max-w-[150px]">
                    {user?.email}
                  </p>
                </div>
                
                {/* Profile dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="flex items-center space-x-2 bg-gradient-to-r from-sage-100 to-mint-100 hover:from-sage-200 hover:to-mint-200 rounded-xl px-3 py-2 transition-all duration-300 transform hover:scale-105 shadow-soft"
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-mint-400 to-emerald-500 rounded-lg flex items-center justify-center shadow-soft">
                      <span className="text-white text-sm font-semibold">
                        {user?.email?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <span className="text-forest-700">▼</span>
                  </button>
                  
                  {/* Dropdown menu */}
                  {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 dropdown-menu animation-slide-down z-50">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            navigate('/profile');
                            setIsProfileMenuOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-forest-700 hover:bg-sage-50 transition-colors duration-200"
                        >
                          👤 View Profile
                        </button>
                        <hr className="my-1 border-sage-200" />
                        <button
                          onClick={() => {
                            handleLogout();
                            setIsProfileMenuOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                        >
                          🚪 Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="sm:hidden flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-xl text-sage-600 hover:text-forest-700 hover:bg-sage-100 transition-all duration-300"
              >
                <span className="sr-only">Open main menu</span>
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="sm:hidden border-t border-sage-200/50 bg-gradient-to-r from-white/20 via-sage-50/30 to-emerald-50/20 backdrop-blur-md">
            <div className="pt-2 pb-3 space-y-1 px-4">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2 rounded-xl text-base font-medium transition-all duration-300 ${
                    isActive(item.path)
                      ? 'bg-gradient-to-r from-mint-100 to-emerald-100 text-forest-700 border-l-4 border-mint-400'
                      : 'text-sage-600 hover:bg-sage-50 hover:text-forest-700'
                  }`}
                >
                  <span className="mr-3 text-lg">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
            
            {/* Mobile user info */}
            <div className="pt-4 pb-3 border-t border-sage-200">
              <div className="px-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-mint-400 to-emerald-500 rounded-lg flex items-center justify-center shadow-soft">
                    <span className="text-white font-semibold">
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div>
                    <div className="text-base font-medium text-forest-800">Welcome back!</div>
                    <div className="text-sm text-sage-600">{user?.email}</div>
                  </div>
                </div>
              </div>
              <div className="mt-3 px-4 space-y-1">
                <button
                  onClick={() => {
                    navigate('/profile');
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 rounded-xl text-base font-medium text-sage-600 hover:text-forest-700 hover:bg-sage-50 transition-colors duration-200"
                >
                  👤 View Profile
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 rounded-xl text-base font-medium text-red-600 hover:bg-red-50 transition-colors duration-200"
                >
                  🚪 Sign out
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0 animation-fade-in">
          {children}
        </div>
      </main>

      {/* Click outside to close dropdowns */}
      {(isProfileMenuOpen || isMobileMenuOpen) && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => {
            setIsProfileMenuOpen(false);
            setIsMobileMenuOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default Layout;
